import { z } from 'zod';

/**
 * `FinancingApplication.status` — lifecycle enum.
 *
 * Per-offer decisions live on `offers[].decision`; non-lifecycle
 * markers (e.g. `late`, `flagged`) live on `tags[]` so they can
 * coexist with any status without enum churn.
 *
 * Transitions:
 *
 *   carrier submit             → pending_review (admin gate)
 *   admin sends to lenders     → submitted + offers populated
 *   lender sends an offer      → offers[i].decision = offered
 *   every lender declines      → declined  (auto-flip, see below)
 *   carrier picks an offer     → accepted + offers[i].decision='accepted'
 *   admin records funding      → funded + outcome.fundedAt
 *   carrier uploads receipt    → awaiting_repayment_approval
 *   admin approves receipt     → repaid + outcome.repaidAt
 *   admin rejects receipt      → reverts to funded
 *   30+ days past due          → defaulted (admin-marked today)
 *
 * `pending_review` is the default; `cancelled` is terminal at any
 * point. `submitted` ONLY appears after the admin sends to lenders.
 *
 * Auto-flip to `declined`: when the LAST outstanding offer row on a
 * `submitted` application flips to `decision: 'declined'`, the
 * service layer also flips `application.status` to `'declined'`
 * (see `FinancingService.computeAllDeclinedTransition`). This is a
 * terminal "no lender bit" state for the carrier — distinct from
 * `cancelled` (which is carrier- or admin-initiated). An admin can
 * still re-fan-out to more lenders to revive the application; that
 * call (`addLendersToApplicationAPI`-equivalent) is responsible for
 * flipping the status back to `'submitted'`.
 */
export const financingApplicationStatusSchema = z.enum([
  'pending_review',
  'submitted',
  'declined',
  'accepted',
  'funded',
  'awaiting_repayment_approval',
  'repaid',
  'defaulted',
  'cancelled',
]);
export type FinancingApplicationStatus = z.infer<
  typeof financingApplicationStatusSchema
>;

/**
 * Non-lifecycle markers on a `FinancingApplication`. Multiple tags
 * can apply simultaneously without changing `status`. Admin sets
 * these manually today; a future cron sweep flips `late` / `overdue`
 * automatically based on `outcome.fundedAt` + repayment offsets in
 * `FINANCING_CONFIG.repayment`.
 */
export const financingApplicationTagSchema = z.enum([
  'late',
  'overdue',
  'flagged',
]);
export type FinancingApplicationTag = z.infer<
  typeof financingApplicationTagSchema
>;

/**
 * Fleet-size bracket the carrier self-reports on the financing
 * profile. Used by lenders to size the offer envelope.
 */
export const financingFleetTierSchema = z.enum([
  'lt_10',
  '11_20',
  '21_50',
  '51_100',
  '101_200',
  '201_500',
  'gt_500',
]);
export type FinancingFleetTier = z.infer<typeof financingFleetTierSchema>;

/**
 * Annual revenue bracket the carrier self-reports, denominated in
 * SAR. Contiguous brackets — confirmed in the plan §12 "Open
 * decisions".
 */
export const financingRevenueTierSchema = z.enum([
  'lt_1m',
  '1m_3m',
  '3m_6m',
  '6m_8m',
  '8m_10m',
  'gt_10m',
]);
export type FinancingRevenueTier = z.infer<typeof financingRevenueTierSchema>;

/**
 * What the carrier needs financing for. Multi-valued. `invoicing`
 * is the only product live in the pilot; the other values are
 * captured up front so the future product expansions
 * (truck-loan, working-capital, fuel) inherit a stable enum.
 */
export const financingNeedSchema = z.enum([
  'invoicing',
  'trucks',
  'working_capital',
  'fuel',
  'other',
]);
export type FinancingNeed = z.infer<typeof financingNeedSchema>;

/**
 * Lifecycle state of one offer row on an application.
 *
 * Linear-ish progression:
 *
 *     pending ─► interested ─► offered ─► accepted
 *         \         \             │
 *          \         └────────► declined
 *           └────────────────► declined
 *
 * - `pending`     — packet fanned out but the lender hasn't engaged.
 * - `interested`  — lender clicked "I'm interested" on their
 *                   partner-view. Contact-masking on the partner
 *                   projection lifts at this point so the lender can
 *                   reach the applicant; terms have NOT been
 *                   submitted yet. Mirrors `offer.interestedAt`.
 * - `offered`     — lender submitted concrete terms.
 * - `accepted`    — carrier chose this offer. Locks the application
 *                   into `status: 'accepted'`.
 * - `declined`    — lender said no. Terminal for this row; the
 *                   application can still complete on another row.
 *
 * Write permissions (enforced at the service layer):
 *
 *  - **Lender** writes `pending → interested → offered | declined`
 *    via the public partner-view (`/financing/partner/:i`) or the
 *    embedded lender-view route. The lender CAN NOT write
 *    `accepted` — that's the carrier's privilege only.
 *  - **Carrier or super-admin** writes `offered → accepted` via
 *    `acceptOfferAPI` (carrier-side) or `markOutcomeAPI` soft-
 *    accept (admin-side, when recording funding). Only one offer
 *    per application can carry `decision: 'accepted'`; the accept
 *    service enforces that invariant.
 *
 * Why `interested` is a decision (and not just the
 * `offer.interestedAt` timestamp): the carrier dashboard and admin
 * panel render offer rows by status pill, so adding a first-class
 * "Interested" state surfaces lender engagement without inventing
 * a parallel "has-stamp-but-no-terms" branch in every UI. The
 * `interestedAt` timestamp is still stamped alongside the decision
 * flip — the field is the audit-trail when, the decision is the
 * live state machine.
 *
 * Why a single field instead of a separate `acceptedOfferId`
 * pointer at the application level: keeping the chosen-offer flag
 * on the offer row itself collapses what used to be two sources of
 * truth ("which offer is accepted?" vs. "what's this offer's
 * state?") into one. Per-lender queries — "applications where
 * lender X won" — become a clean
 * `{ offers: { $elemMatch: { 'lender._id': X, decision: 'accepted' } } }`
 * lookup against the existing `{ 'offers.lender._id': 1 }` index.
 *
 * Naming note — this enum used to spell the lender's "submitted
 * terms" state as `'approved'`, which was confusing because it
 * implied the *application* was approved. The lender's action is
 * sending an offer, not approving anything; `offered` makes that
 * split explicit so the application-level `'accepted'` status and
 * the offer-level `'accepted'` decision can coexist without
 * confusion (status tracks the contract; decision tracks one
 * lender's row).
 */
export const financingOfferDecisionSchema = z.enum([
  'pending',
  'interested',
  'offered',
  'declined',
  'accepted',
]);

/**
 * Subset of {@link financingOfferDecisionSchema} that lenders are
 * allowed to write on themselves via the partner-view / lender-view
 * submit endpoints. Excludes `accepted` — only the carrier or a
 * super-admin acting on their behalf can flip an offer to
 * `accepted`. Excludes `interested` because the lender doesn't
 * pass it as a parameter; the dedicated `partnerExpressInterestAPI`
 * / `lenderExpressInterestAPI` endpoints write that transition
 * server-side alongside the `interestedAt` stamp.
 */
export const lenderWritableOfferDecisionSchema = z.enum([
  'offered',
  'declined',
]);
export type LenderWritableOfferDecision = z.infer<
  typeof lenderWritableOfferDecisionSchema
>;
export type FinancingOfferDecision = z.infer<
  typeof financingOfferDecisionSchema
>;

/**
 * Stock decline reasons surfaced in the lender's reject modal. The
 * lender always picks one of these plus free-text notes. We keep
 * the enum small for v0 — additions land here as the pilot reveals
 * new patterns.
 */
export const financingDeclineReasonSchema = z.enum([
  'risk_too_high',
  'incomplete_profile',
  'customer_concentration',
  'outside_criteria',
  'other',
]);
export type FinancingDeclineReason = z.infer<
  typeof financingDeclineReasonSchema
>;

/**
 * Source of the invoice being financed. `hero` references an
 * existing `Invoice._id`; `external` carries an uploaded PDF +
 * free-text customer details and is what enables financing-led
 * acquisition for fresh signups.
 */
export const financingInvoiceSourceSchema = z.enum(['hero', 'external']);
export type FinancingInvoiceSource = z.infer<
  typeof financingInvoiceSourceSchema
>;
